import { useState, useEffect } from "react";
import { api } from "@/App";
import { 
  Trophy, 
  Flame,
  Zap,
  Target,
  BookOpen,
  Brain,
  Award,
  Circle,
  Compass,
  Footprints,
  Lock
} from "lucide-react";

const iconMap = {
  Footprints,
  Flame,
  Zap,
  Trophy,
  BookOpen,
  Brain,
  Target,
  Compass,
  Award,
  Circle,
};

const AchievementsPage = () => {
  const [achievements, setAchievements] = useState([]);
  const [earnedIds, setEarnedIds] = useState([]);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [achievementsData, progressData] = await Promise.all([
          api.get("/achievements"),
          api.get("/progress")
        ]);
        setAchievements(achievementsData.achievements);
        setProgress(progressData.progress);
        setEarnedIds(progressData.progress?.achievements || []);
      } catch (error) {
        console.error("Failed to fetch achievements:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading achievements...</p>
        </div>
      </div>
    );
  }

  const earnedAchievements = achievements.filter(a => earnedIds.includes(a.id));
  const lockedAchievements = achievements.filter(a => !earnedIds.includes(a.id));
  const totalXpFromAchievements = earnedAchievements.reduce((sum, a) => sum + a.xp_reward, 0);

  const tierOrder = ['platinum', 'gold', 'silver', 'bronze'];
  const sortByTier = (a, b) => tierOrder.indexOf(a.tier) - tierOrder.indexOf(b.tier);

  return (
    <div data-testid="achievements-page" className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Achievements</h1>
        <p className="text-muted-foreground">Unlock rewards as you progress</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="glass rounded-2xl p-6 border border-border text-center">
          <Trophy className="w-8 h-8 text-warning mx-auto mb-2" />
          <p className="text-3xl font-bold">{earnedAchievements.length}</p>
          <p className="text-sm text-muted-foreground">Earned</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-border text-center">
          <Lock className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-3xl font-bold">{lockedAchievements.length}</p>
          <p className="text-sm text-muted-foreground">Locked</p>
        </div>
        <div className="glass rounded-2xl p-6 border border-border text-center">
          <Zap className="w-8 h-8 text-primary mx-auto mb-2" />
          <p className="text-3xl font-bold">{totalXpFromAchievements}</p>
          <p className="text-sm text-muted-foreground">XP Earned</p>
        </div>
      </div>

      {/* Earned Achievements */}
      {earnedAchievements.length > 0 && (
        <div>
          <h2 className="text-xl font-semibold mb-4">Earned ({earnedAchievements.length})</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {earnedAchievements.sort(sortByTier).map((achievement) => {
              const IconComponent = iconMap[achievement.icon] || Trophy;
              return (
                <div
                  key={achievement.id}
                  className={`glass rounded-2xl p-6 border transition-all hover-lift
                    ${achievement.tier === 'platinum' ? 'border-[#E5E4E2]/30 bg-[#E5E4E2]/5' :
                      achievement.tier === 'gold' ? 'border-warning/30 bg-warning/5' :
                      achievement.tier === 'silver' ? 'border-gray-400/30 bg-gray-400/5' :
                      'border-amber-700/30 bg-amber-700/5'}
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl tier-${achievement.tier}
                      ${achievement.tier === 'platinum' ? 'bg-[#E5E4E2]/20' :
                        achievement.tier === 'gold' ? 'bg-warning/20' :
                        achievement.tier === 'silver' ? 'bg-gray-400/20' :
                        'bg-amber-700/20'}
                    `}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold">{achievement.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">{achievement.description}</p>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full capitalize
                          ${achievement.tier === 'platinum' ? 'bg-[#E5E4E2]/20 text-[#E5E4E2]' :
                            achievement.tier === 'gold' ? 'bg-warning/20 text-warning' :
                            achievement.tier === 'silver' ? 'bg-gray-400/20 text-gray-400' :
                            'bg-amber-700/20 text-amber-700'}
                        `}>
                          {achievement.tier}
                        </span>
                        <span className="text-xs text-primary font-medium">
                          +{achievement.xp_reward} XP
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Locked Achievements */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Locked ({lockedAchievements.length})</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lockedAchievements.sort(sortByTier).map((achievement) => {
            const IconComponent = iconMap[achievement.icon] || Trophy;
            return (
              <div
                key={achievement.id}
                className="glass rounded-2xl p-6 border border-border opacity-60"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-secondary">
                    <IconComponent className="w-6 h-6 text-muted-foreground" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold">{achievement.name}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{achievement.description}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-secondary text-muted-foreground capitalize">
                        {achievement.tier}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        +{achievement.xp_reward} XP
                      </span>
                    </div>
                  </div>
                  <Lock className="w-4 h-4 text-muted-foreground" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AchievementsPage;
